import {
	Card,
	CardActions,
	CardContent,
	Grid,
	TableContainer,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	useMediaQuery,
	useTheme,
	Box,
} from '@mui/material';
import React from 'react';
import {
	CerebroCheckbox,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroIcon,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroSecondaryButton,
	CerebroStatusIndicatorTableCell,
	CerebroStyledBox,
	CerebroText,
	CerebroTextButton,
	LenderStoreDot,
	StatusIndicatorColor,
} from '../../shared/components';
import { useDataRoomConfirmationQuery } from '../../shared/hooks';
import { CurrentUser, DataRoomTeaser, StructureTranches, Tier1BankTypeMap } from '../../shared/models';
import { DateUtils, EnumUtils } from '../../shared/utils';
import { TeaserConfirmationDialog } from './TeaserConfirmationDialog';
import { TeaserActionButton } from './TeaserActionButton';
import { faLanguage, faBan } from '@fortawesome/pro-duotone-svg-icons';
import { faCommentDollar } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessageSms } from '@fortawesome/pro-solid-svg-icons';

interface TeaserListProps {
	dataRoomGuid?: string;
	teasers?: DataRoomTeaser[];
	currentUser: CurrentUser;
	tab: string;
	selectedTeasers?: string[] | null;
	usedTranches: StructureTranches[];
	hasLaunched: boolean;
	setSelectedTeasers: (teasers?: string[]) => void;
	respondTeaser: (teaser: DataRoomTeaser) => void;
	sendTeaser: (teaser: DataRoomTeaser) => void;
	editTeaser: (teaser: DataRoomTeaser) => void;
	removeTeaser: (teaser: DataRoomTeaser) => void;
	resetTeaser: (teaser: DataRoomTeaser) => void;
	viewTeaserDetails: (teaser: DataRoomTeaser) => void;
	restoreTeaser: (teaser: DataRoomTeaser) => void;
	onToggleReferralLanguage: (teaser: DataRoomTeaser) => void;
	onIgnoreLaunch: (teaser: DataRoomTeaser) => void;
	teasersReady: boolean;
}
export type TeaserSortBy = 'Lender' | 'Contact' | 'SentOn' | 'Tranche';

export const TeaserList: React.FC<TeaserListProps> = ({
	dataRoomGuid,
	teasers,
	currentUser,
	tab,
	usedTranches,
	hasLaunched,
	respondTeaser,
	sendTeaser,
	editTeaser,
	removeTeaser,
	resetTeaser,
	viewTeaserDetails,
	onToggleReferralLanguage,
	selectedTeasers,
	setSelectedTeasers,
	restoreTeaser,
	teasersReady,
	onIgnoreLaunch,
}) => {
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
	const [bankNameFilter, setBankNameFilter] = React.useState<string>();
	const [contactLastNameFilter, setContactLastNameFilter] = React.useState<string>();
	const [tier1TypeFilter, setTier1TypeFilter] = React.useState<string>('Select Bank Type');
	const [trancheFilter, setTrancheFilter] = React.useState<string>('Select Tranche');
	const [filterResult, setFilterResult] = React.useState<DataRoomTeaser[]>(teasers ?? ([] as DataRoomTeaser[]));
	const [previousTeasers, setPreviousTeasers] = React.useState<DataRoomTeaser[]>(teasers ?? ([] as DataRoomTeaser[]));
	const [sortFilter, setSortFilter] = React.useState<TeaserSortBy>('Lender');
	const confirmationQuery = useDataRoomConfirmationQuery(dataRoomGuid);
	const [confirmDialog, setConfirmDialog] = React.useState<boolean>(false);
	const isPendingTab = tab == 'sent' || tab == 'unsent';

	const onTeaserSelected = (guid: string) => {
		let updatedList = selectedTeasers?.map(t => t);

		if (selectedTeasers?.find(w => w == guid)) {
			updatedList = selectedTeasers.filter(t => t != guid);
		} else {
			updatedList?.push(guid);
		}

		setSelectedTeasers(updatedList ?? []);
	};

	const applyFilter = () => {
		let result = teasers?.map(t => t) ?? ([] as DataRoomTeaser[]);

		if (!!bankNameFilter && bankNameFilter !== 'Bank Name')
			result = result.filter(t => t.bankName.toLocaleLowerCase().includes(bankNameFilter.toLocaleLowerCase()));

		if (!!contactLastNameFilter && contactLastNameFilter !== 'Contact Last Name')
			result = result.filter(t =>
				t.lastName.toLocaleLowerCase().includes(contactLastNameFilter.toLocaleLowerCase())
			);

		if (!!tier1TypeFilter && tier1TypeFilter !== 'Select Bank Type')
			result = result.filter(t => t.tier1Type === tier1TypeFilter);

		if (!!trancheFilter && trancheFilter !== 'Select Tranche')
			result = result.filter(t => t.structureType === trancheFilter);

		switch (sortFilter) {
			case 'Lender':
				result = result.sort((a, b) => a.bankName.localeCompare(b.bankName));
				break;
			case 'Contact':
				result = result.sort((a, b) =>
					(a.firstName + ' ' + a.lastName).localeCompare(b.firstName + ' ' + b.lastName)
				);
				break;
			case 'SentOn':
				result = result.sort((a, b) =>
					(a.sentOn ?? new Date('01/01/1971')) > (b.sentOn ?? new Date('01/01/1971'))
						? 1
						: (a.sentOn ?? new Date('01/01/1971')) < (b.sentOn ?? new Date('01/01/1971'))
						? -1
						: 0
				);
				break;
			case 'Tranche':
				result = result.sort((a, b) =>
					(EnumUtils.GetStructureTrancheDescription(a.structureType) ?? 'Unknown').localeCompare(
						EnumUtils.GetStructureTrancheDescription(b.structureType) ?? 'Unknown'
					)
				);
				break;
		}

		setFilterResult(result);
	};

	const onResetClick = () => {
		setFilterResult(teasers ?? ([] as DataRoomTeaser[]));
		setBankNameFilter('');
		setContactLastNameFilter('');
		setTier1TypeFilter('Select Bank Type');
	};

	const getStatusColor = (teaser: DataRoomTeaser): StatusIndicatorColor => {
		if (teaser.decisionStatus == 'Accepted') {
			return 'Green';
		}

		return teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent' ? 'Yellow' : 'Red';
	};

	const getStatusTooltip = (teaser: DataRoomTeaser): string => {
		if (teaser.decisionStatus == 'Accepted') {
			return 'Active';
		}

		return teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent' ? 'Pending' : 'Declined';
	};

	// force rerender of teaser list if state changes
	if (previousTeasers !== teasers) {
		setPreviousTeasers(teasers ?? []);
		applyFilter();
	}

	return (
		<>
			<Grid container spacing={2}>
				<Grid item xs={12} lg={2}>
					<CerebroInput
						placeholder="Bank Name"
						value={bankNameFilter}
						onChange={data => setBankNameFilter(data.target.value)}
					/>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroInput
						placeholder="Contact Last Name"
						value={contactLastNameFilter}
						onChange={data => setContactLastNameFilter(data.target.value)}
					/>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={tier1TypeFilter}
						onChange={data => setTier1TypeFilter(data.target.value as string)}
					>
						<CerebroDropdownMenuItem value="Select Bank Type">Bank Type</CerebroDropdownMenuItem>
						{Object.entries(Tier1BankTypeMap).map(([value, description]) => {
							return (
								<CerebroDropdownMenuItem key={value} value={value}>
									{description}
								</CerebroDropdownMenuItem>
							);
						})}
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={trancheFilter}
						onChange={data => setTrancheFilter(data.target.value as string)}
					>
						<CerebroDropdownMenuItem value="Select Tranche">Tranche</CerebroDropdownMenuItem>
						{usedTranches.map(item => (
							<CerebroDropdownMenuItem key={item} value={item}>
								{EnumUtils.GetStructureTrancheDescription(item)}
							</CerebroDropdownMenuItem>
						))}
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroDropdown
						value={sortFilter}
						onChange={data => setSortFilter(data.target.value as TeaserSortBy)}
					>
						<CerebroDropdownMenuItem key="Lender" value="Lender">
							Lender Sort
						</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem key="Contact" value="Contact">
							Contact Sort
						</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem key="SentOn" value="SentOn">
							Sent On Sort
						</CerebroDropdownMenuItem>
						<CerebroDropdownMenuItem key="Tranche" value="Tranche">
							Tranche Sort
						</CerebroDropdownMenuItem>
					</CerebroDropdown>
				</Grid>
				<Grid item xs={12} lg={2}>
					<CerebroPrimaryButton sx={{ marginTop: 1.5, marginRight: 2 }} onClick={() => applyFilter()}>
						Filter
					</CerebroPrimaryButton>

					<CerebroSecondaryButton sx={{ marginTop: 1.5 }} onClick={() => onResetClick()}>
						Reset
					</CerebroSecondaryButton>
				</Grid>
			</Grid>

			{(filterResult == null || filterResult.length == 0 || dataRoomGuid == null) && (
				<CerebroStyledBox>
					<CerebroText sx={{ marginBottom: 2 }}>
						There are no teasers that currently matching this filter.
					</CerebroText>
				</CerebroStyledBox>
			)}

			{filterResult != null && filterResult.length > 0 && dataRoomGuid && (
				<>
					{isMobile ? (
						<Grid container spacing={2} alignItems="stretch">
							{filterResult.map(teaser => {
								const isPending = teaser.decisionStatus == 'Unsent' || teaser.decisionStatus == 'Sent';

								return (
									<Grid key={teaser.guid} item xs={12}>
										<Card
											key={teaser.guid}
											elevation={3}
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												flexDirection: 'column',
												height: '100%',
												marginBottom: 2,
											}}
										>
											<CardContent sx={{ paddingBottom: '0 !important' }}>
												<Grid container sx={{ marginBottom: 2 }}>
													<Grid item xs={8} sx={{ display: 'flex' }}>
														{isPendingTab && (
															<CerebroCheckbox
																value={!!selectedTeasers?.find(t => t === teaser.guid)}
																onClick={() => onTeaserSelected(teaser.guid)}
															></CerebroCheckbox>
														)}
														{teaser.includeReferralLanguage && (
															<CerebroIcon
																padRight
																sx={theme => ({ color: theme.palette.info.main })}
																title="Include Referral Language"
															>
																<FontAwesomeIcon icon={faLanguage} size="sm" />
															</CerebroIcon>
														)}
														<CerebroLabel sx={{ fontWeight: 'bold', alignSelf: 'center' }}>
															{teaser.bankName}
															{teaser.affiliateLender && (
																<CerebroText component="span" variant="hint">
																	&nbsp;({teaser.affiliateLender})
																</CerebroText>
															)}
														</CerebroLabel>
													</Grid>
													<Grid item xs={4} sx={{ textAlign: 'right' }}>
														<LenderStoreDot
															score={teaser.score}
															isFlagged={teaser.lenderScoreFlag}
															sx={{ marginRight: 0.5 }}
														/>
														<CerebroText
															variant="large"
															component="span"
															sx={{ fontWeight: 'bold' }}
														>
															{teaser.firstName} {teaser.lastName}
														</CerebroText>
													</Grid>
												</Grid>
												<CerebroText gutterBottom sx={{ marginTop: 1 }}>
													{teaser.hasFinderFeeAgreement && (
														<CerebroIcon
															padRight
															sx={theme => ({ color: theme.palette.success.main })}
															title="Has Finder Fee Agreements"
														>
															<FontAwesomeIcon icon={faCommentDollar} size="sm" />
														</CerebroIcon>
													)}
													{EnumUtils.GetStructureTrancheDescription(teaser.structureType)}
												</CerebroText>
												<CerebroText gutterBottom>
													{teaser.sentOn && teaser.decisionOn && (
														<>
															Last sent on{' '}
															{DateUtils.FormatStringAsShortDate(
																teaser.sentOn.toString()
															)}
															, {teaser.decisionStatus} on{' '}
															{DateUtils.FormatStringAsShortDate(
																teaser.decisionOn.toString()
															)}
															.
														</>
													)}
													{teaser.sentOn && !teaser.decisionOn && (
														<>
															Last sent on{' '}
															{DateUtils.FormatStringAsShortDate(
																teaser.sentOn.toString()
															)}
														</>
													)}
													{!teaser.sentOn && <>Not sent.</>}
												</CerebroText>
												{teaser.decisionStatus == 'Declined' && (
													<>
														<CerebroText sx={{ marginTop: 1 }}>
															<strong>Decline Reasons</strong>
														</CerebroText>
														{teaser.lenderDeclineReasons &&
															teaser.lenderDeclineReasons.length > 0 &&
															teaser.lenderDeclineReasons.map(reason => (
																<CerebroText key={reason}>{reason}</CerebroText>
															))}
														{teaser.otherDeclineReason && (
															<CerebroText>{teaser.otherDeclineReason}</CerebroText>
														)}
													</>
												)}
											</CardContent>
											{currentUser.isEmployeeUser && (
												<CardActions
													sx={theme => ({
														alignItems: 'flex-end',
														backgroundColor: theme.palette.gray.background,
														borderTop: `1px solid ${theme.palette.divider}`,
														justifyContent: 'space-between',
													})}
												>
													{isPending && (
														<>
															<CerebroTextButton
																size="small"
																onClick={() => respondTeaser(teaser)}
															>
																Respond
															</CerebroTextButton>

															<CerebroTextButton
																size="small"
																onClick={() => sendTeaser(teaser)}
															>
																{teaser.sentOn ? 'Resend' : 'Send'}
															</CerebroTextButton>

															<CerebroTextButton
																size="small"
																onClick={() => editTeaser(teaser)}
															>
																Tranche
															</CerebroTextButton>

															{!teaser.sentOn && (
																<CerebroTextButton
																	size="small"
																	onClick={() => removeTeaser(teaser)}
																>
																	Remove
																</CerebroTextButton>
															)}
														</>
													)}
													{isPending && (
														<CerebroTextButton
															size="small"
															onClick={() => resetTeaser(teaser)}
														>
															Reset
														</CerebroTextButton>
													)}
													<CerebroTextButton
														size="small"
														onClick={() => viewTeaserDetails(teaser)}
													>
														Details
													</CerebroTextButton>
												</CardActions>
											)}
										</Card>
									</Grid>
								);
							})}

							{tab == 'unsent' &&
								confirmationQuery.isSuccess &&
								confirmationQuery.data.hasUnsentTeasers &&
								!confirmationQuery.data.teaserListConfirmed && (
									<Grid item sx={{ textAlign: 'center', marginTop: 3 }}>
										<CerebroPrimaryButton onClick={() => setConfirmDialog(true)}>
											Confirm Teaser List
										</CerebroPrimaryButton>
									</Grid>
								)}
						</Grid>
					) : (
						<>
							<TableContainer>
								<Table>
									<TableHead>
										<TableRow>
											<TableCell sx={{ width: '5px', padding: '0px' }}></TableCell>
											{isPendingTab && <TableCell></TableCell>}
											<TableCell>Lender</TableCell>
											<TableCell>Lender Type</TableCell>
											<TableCell>Contact</TableCell>
											<TableCell>Sent On</TableCell>
											<TableCell>Tranche</TableCell>
											{currentUser.isEmployeeUser && <TableCell>Actions</TableCell>}
										</TableRow>
									</TableHead>
									<TableBody>
										{filterResult.map(teaser => {
											return (
												<TableRow key={teaser.guid}>
													<CerebroStatusIndicatorTableCell
														colorType={getStatusColor(teaser)}
														tooltip={getStatusTooltip(teaser)}
													/>
													{isPendingTab && (
														<TableCell>
															<CerebroCheckbox
																value={!!selectedTeasers?.find(t => t === teaser.guid)}
																onClick={() => onTeaserSelected(teaser.guid)}
															></CerebroCheckbox>
														</TableCell>
													)}
													<TableCell>
														{teaser.includeReferralLanguage && (
															<CerebroIcon
																padRight
																sx={theme => ({ color: theme.palette.info.main })}
																title="Include Referral Language"
															>
																<FontAwesomeIcon icon={faLanguage} size="sm" />
															</CerebroIcon>
														)}
														{teaser.bankName}
														{teaser.affiliateLender && (
															<CerebroText component="p" variant="hint">
																({teaser.affiliateLender})
															</CerebroText>
														)}
													</TableCell>
													<TableCell>
														{EnumUtils.GetTier1BankTypeDescription(teaser.tier1Type)}
													</TableCell>
													<TableCell>
														{teaser.receiveTeasersSms && (
															<CerebroIcon
																padRight
																sx={theme => ({ color: theme.palette.info.main })}
																title="Receive Teaser Sms"
															>
																<FontAwesomeIcon icon={faMessageSms} size="sm" />
															</CerebroIcon>
														)}
														{teaser.firstName} {teaser.lastName}
													</TableCell>
													<TableCell>
														{teaser.decisionStatus == 'Unsent' && teaser.ignoreAtLaunch && (
															<CerebroIcon
																padRight
																sx={theme => ({ color: theme.palette.warning.main })}
																title="Ignore at Launch"
															>
																<FontAwesomeIcon icon={faBan} size="sm" />
															</CerebroIcon>
														)}
														{teaser.sentOn && (
															<>
																{DateUtils.FormatStringAsShortDate(
																	teaser.sentOn.toString()
																)}
															</>
														)}
														{!teaser.sentOn && <>Not sent.</>}
													</TableCell>
													<TableCell>
														{teaser.hasFinderFeeAgreement && (
															<CerebroIcon
																padRight
																sx={theme => ({ color: theme.palette.success.main })}
																title="Has Finder Fee Agreements"
															>
																<FontAwesomeIcon icon={faCommentDollar} size="sm" />
															</CerebroIcon>
														)}
														{EnumUtils.GetStructureTrancheDescription(teaser.structureType)}
													</TableCell>
													{currentUser.isEmployeeUser && (
														<TableCell>
															<TeaserActionButton
																teasersReady={teasersReady}
																teaser={teaser}
																tab={tab}
																hasLaunched={hasLaunched}
																onEdit={editTeaser}
																onRespond={respondTeaser}
																onSend={sendTeaser}
																onRemove={removeTeaser}
																onReset={resetTeaser}
																onViewDetails={viewTeaserDetails}
																onRestore={restoreTeaser}
																onToggleReferralLanguage={onToggleReferralLanguage}
																onIgnoreLaunch={onIgnoreLaunch}
															/>
														</TableCell>
													)}
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>

							{tab == 'unsent' &&
								confirmationQuery.isSuccess &&
								confirmationQuery.data.hasUnsentTeasers &&
								!confirmationQuery.data.teaserListConfirmed && (
									<Box sx={{ textAlign: 'center', marginTop: 3 }}>
										<CerebroPrimaryButton onClick={() => setConfirmDialog(true)}>
											Confirm Teaser List
										</CerebroPrimaryButton>
									</Box>
								)}
						</>
					)}
				</>
			)}

			{tab == 'unsent' && (
				<TeaserConfirmationDialog
					dataRoomGuid={dataRoomGuid}
					isOpen={confirmDialog}
					close={() => setConfirmDialog(false)}
				/>
			)}
		</>
	);
};
